// Apolo IDE — ChatErrorBoundary (harness-audit E2).
//
// The conversation feed renders fully untrusted, model-shaped data (markdown,
// tool args/results, reasoning). A throw in MarkdownMessage / ToolCallBlock used
// to unmount the WHOLE app to a blank screen ("tela preta = throw no render")
// because there was no ErrorBoundary anywhere near the feed (App.jsx/main.jsx
// have none). This boundary localizes the blast radius to the history area: the
// composer stays alive and the user can recover. `resetKey` (the message count)
// auto-clears the error when a new turn arrives, so a single bad message doesn't
// permanently wedge the feed.

import { Component } from 'react';

export default class ChatErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ChatErrorBoundary] feed render falhou', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // New turn (resetKey changed) → retry rendering; if the offending message is
    // gone the feed recovers on its own.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          className="m-4 rounded-apolo-md border border-apolo-error/40 bg-apolo-surface1 p-4 text-sm text-apolo-textSecondary"
        >
          <p className="font-medium text-apolo-textPrimary">Este turno falhou ao renderizar.</p>
          <p className="mt-1 text-apolo-textTertiary">
            O resto da sessão segue funcionando — o erro foi contido aqui no histórico, sem tela preta.
          </p>
          <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-apolo-surface2 p-2 text-xs text-apolo-textTertiary">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <button
            type="button"
            onClick={this.reset}
            className="mt-3 inline-flex h-8 items-center rounded-apolo-md border border-apolo-borderSubtle px-3 text-xs font-medium text-apolo-textPrimary transition hover:bg-apolo-surface2 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
          >
            Tentar renderizar de novo
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
